/**
 * batch-70 app-dashboard (FO-526) - wanneer landde elke CHANGELOG-sectie op main, uit de
 * uitvoer van `git log --first-parent -m -p --reverse --format='@@C %H %cI' -- CHANGELOG.md`.
 *
 * Puur (de git-aanroep zit in het script `app-dashboard-bronnen.ts`), zodat de lastige
 * gevallen getest zijn:
 *   - een nieuwe sectie landt bij de commit die hem toevoegde;
 *   - een bijgewerkte kop met een FO houdt de landing van de oude als datum en FO's gelijk
 *     zijn (ook als de kop elders in de commit staat);
 *   - een bijgewerkte kop zonder FO houdt de landing (en geeft zijn vorige sleutels mee,
 *     zodat de rij dezelfde blijft) als hij in hetzelfde wijzigingsblok de enige
 *     verwijderde kop van die datum vervangt en de woorden grotendeels gelijk zijn;
 *   - een nieuwe sectie met dezelfde datum boven een bijgewerkte kop erft niets. Git zet
 *     in een blok eerst alle min- en dan alle plusregels: koppelen op datum alleen zou de
 *     nieuwe sectie de landing van de oude kop geven.
 */
import { changelogSectieSleutel, foNummersUitTekst } from './changelog'
import { woordOverlap } from './specs'

export interface SectieLanding {
  /** Het moment (ISO, met offset) van de commit op main die de sectie toevoegde. */
  op: string
  /** De positie van die commit in de eerste-ouderketen (-1 = onbekend). */
  index: number
}

export interface ChangelogLandingen {
  /** Per sectiesleutel (`cl-...`) de landing. */
  landingen: Map<string, SectieLanding>
  /** Per sectiesleutel de sleutels van dezelfde kop voordat hij werd bijgewerkt (nieuwste eerst). */
  vorige: Map<string, string[]>
}

const DATUM_RE = /^(\d{4}-\d{2}-\d{2})\s*(?:[-:\u2013\u2014]\s*)?/

/** Zoveel van de woorden van de kortste kop moet de andere ook hebben (een herformulering, geen andere sectie). */
export const KOP_WOORDEN_DREMPEL = 0.5

function datumVan(kop: string): string | null {
  return DATUM_RE.exec(kop.trim())?.[1] ?? null
}

/** Datum en FO's van een kop met een FO: dezelfde sectie als alleen de titel veranderde. */
function kopIdentiteit(kop: string): string | null {
  const datum = datumVan(kop)
  const fo = foNummersUitTekst(kop)
  return datum && fo.length > 0 ? `${datum}|${[...fo].sort((a, b) => a - b).join(',')}` : null
}

/** Het deel van de woorden dat twee koppen (zonder datum) delen, van de kortste uit gezien. */
export function kopGelijkenis(a: string, b: string): number {
  const x = a.trim().replace(DATUM_RE, '')
  const y = b.trim().replace(DATUM_RE, '')
  const kleinste = Math.min(woordOverlap(x, x), woordOverlap(y, y))
  return kleinste === 0 ? 0 : woordOverlap(x, y) / kleinste
}

/**
 * De koppen zonder FO die in een wijzigingsblok zijn vervangen: per datum precies één
 * verwijderde kop, en de toegevoegde kop die er duidelijk het meest op lijkt.
 */
function vervangenZonderFo(eraf: readonly string[], erbij: readonly string[]): Map<string, string> {
  const perDatum = (koppen: readonly string[]) => {
    const m = new Map<string, string[]>()
    for (const k of koppen) {
      const d = datumVan(k)
      if (d && foNummersUitTekst(k).length === 0) m.set(d, [...(m.get(d) ?? []), k])
    }
    return m
  }
  const paren = new Map<string, string>()
  const toegevoegd = perDatum(erbij)
  for (const [datum, oud] of perDatum(eraf)) {
    const [enige] = oud
    const nieuw = toegevoegd.get(datum)
    if (oud.length !== 1 || enige === undefined || !nieuw) continue
    const scores = nieuw.map((k) => ({ k, score: kopGelijkenis(enige, k) })).sort((p, q) => q.score - p.score)
    const [beste, tweede] = scores
    if (beste && beste.score >= KOP_WOORDEN_DREMPEL && (!tweede || tweede.score < beste.score)) paren.set(beste.k, enige)
  }
  return paren
}

export function landingenUitChangelogLog(log: string, index: ReadonlyMap<string, number>): ChangelogLandingen {
  const landingen = new Map<string, SectieLanding>()
  const vorige = new Map<string, string[]>()
  let commit: { sha: string; op: string } | null = null
  let erbij: string[] = []
  let eraf: string[] = []
  let blokEraf: string[] = []
  let blokErbij: string[] = []
  let paren = new Map<string, string>()

  const sluitBlok = () => {
    for (const [nieuw, oud] of vervangenZonderFo(blokEraf, blokErbij)) paren.set(nieuw, oud)
    blokEraf = []
    blokErbij = []
  }
  const verwerk = () => {
    sluitBlok()
    if (!commit) return
    const idx = index.get(commit.sha) ?? -1
    const weg = new Map<string, SectieLanding>()
    for (const kop of eraf) {
      const id = kopIdentiteit(kop)
      const l = landingen.get(changelogSectieSleutel(kop))
      if (id && l) weg.set(id, l)
    }
    for (const kop of erbij) {
      const sleutel = changelogSectieSleutel(kop)
      if (landingen.has(sleutel)) continue
      const id = kopIdentiteit(kop)
      const oud = paren.get(kop)
      const oudeSleutel = oud !== undefined ? changelogSectieSleutel(oud) : null
      const geerfd = (oudeSleutel ? landingen.get(oudeSleutel) : undefined) ?? (id ? weg.get(id) : undefined)
      landingen.set(sleutel, geerfd ?? { op: commit.op, index: idx })
      if (oudeSleutel && oudeSleutel !== sleutel && geerfd) {
        vorige.set(sleutel, [oudeSleutel, ...(vorige.get(oudeSleutel) ?? [])].slice(0, 20))
      }
    }
  }

  for (const regel of log.split('\n')) {
    if (regel.startsWith('@@C ')) {
      verwerk()
      const [, sha = '', op = ''] = regel.split(' ')
      commit = { sha, op }
      erbij = []
      eraf = []
      paren = new Map()
    } else if (regel.startsWith('+## ')) {
      const kop = regel.slice(4)
      erbij.push(kop)
      blokErbij.push(kop)
    } else if (regel.startsWith('-## ')) {
      const kop = regel.slice(4)
      eraf.push(kop)
      blokEraf.push(kop)
    } else if (!regel.startsWith('+') && !regel.startsWith('-')) {
      // Een contextregel of een nieuw hunk: het wijzigingsblok is voorbij.
      sluitBlok()
    }
  }
  verwerk()
  return { landingen, vorige }
}
